import type { Session, User } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import type { UserRole } from '../../types/domain';
import { normalizeUserRole } from './permissions';
import { updateOwnDisplayName } from './profile';

type AuthState = {
  session: Session | null;
  user: User | null;
  role: UserRole | null;
  fullName: string;
  position: string;
  active: boolean;
  loading: boolean;
  canManageAccounts: boolean;
  updateDisplayName: (fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [fullName, setFullName] = useState('');
  const [position, setPosition] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [canManageAccounts, setCanManageAccounts] = useState(false);

  useEffect(() => {
    let mounted = true;
    let revision = 0;
    let profileUserId: string | null = null;
    const hydrateProfile = async (nextSession: Session | null) => {
      const currentRevision = ++revision;
      if (!mounted) return;
      setSession(nextSession);
      const nextUserId = nextSession?.user.id ?? null;
      if (nextUserId !== profileUserId) {
        profileUserId = nextUserId;
        setLoading(true);
        setRole(null);
        setCanManageAccounts(false);
        setFullName('');
        setPosition('');
        setActive(false);
      }
      if (nextSession?.user) {
        const [{ data }, access] = await Promise.all([
          supabase.from('profiles').select('role, full_name, position, active').eq('id', nextSession.user.id).maybeSingle(),
          supabase.rpc('can_manage_accounts'),
        ]);
        if (mounted && currentRevision === revision) {
          setRole(normalizeUserRole(data?.role));
          const metadataLogin = typeof nextSession.user.user_metadata.login === 'string'
            ? nextSession.user.user_metadata.login
            : '';
          setFullName(data?.full_name || metadataLogin || nextSession.user.email || 'Сотрудник');
          setPosition(data?.position ?? '');
          setActive(data?.active ?? true);
          setCanManageAccounts(!access.error && access.data === true && data?.active === true);
        }
      }
      if (mounted && currentRevision === revision) setLoading(false);
    };

    void supabase.auth.getSession().then(({ data }) => hydrateProfile(data.session));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void hydrateProfile(nextSession);
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(() => ({
    session,
    user: session?.user ?? null,
    role,
    fullName,
    position,
    active,
    loading,
    canManageAccounts,
    updateDisplayName: async (nextFullName) => {
      const currentUser = session?.user;
      if (!currentUser) throw new Error('Пользователь не авторизован.');
      const savedName = await updateOwnDisplayName(nextFullName);
      setFullName(savedName);
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  }), [session, role, fullName, position, active, loading, canManageAccounts]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
